import {
    getDefaultBillingFrequency,
    MembershipBillingFrequency,
    SUBSCRIPTION_PRODUCT_BILLING_FREQUENCIES,
    SubscriptionProductId,
} from '@fruitful/types';
import type { StytchError } from '@stytch/vanilla-js';
import { Redirect, router } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import {
    Button,
    ButtonVariant,
    CogIcon,
    Form,
    FullScreenSpinner,
    IconButton,
    Input,
    isWeb,
    LabelRegular,
    Notice,
    PrivateInput,
    Row,
    RowList,
    ScreenContainer,
    SegmentedControl,
    Sheet,
    Switch,
    TrashIcon,
    UserPlusIcon,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { SESSION_DURATION_MINUTES } from '../../auth/constants';
import { useStytch } from '../../auth/hooks/useAuth';
import useChangeTestUserSubscription from '../../auth/hooks/useChangeTestUserSubscription';
import useCreateTestUser from '../../auth/hooks/useCreateTestUser';
import StorageFacade from '../../common/providers/StorageFacade';
import {
    SUCCESS_TOAST_DURATION,
    SuccessToastIcon,
} from '../../member-profile/components/SuccessToast';

const QUICK_SWITCHER_STORAGE_KEY = 'dev_quick_switcher_users';

type UserSheetMode = 'create_user' | 'add_credentials';
type ProductGroup = 'cme' | 'legacy';

const PRODUCTS_BY_GROUP: Record<
    ProductGroup,
    readonly SubscriptionProductId[]
> = {
    cme: [
        SubscriptionProductId.TRIAL_PREMIUM,
        SubscriptionProductId.PREMIUM,
        SubscriptionProductId.STANDARD,
    ],
    legacy: [SubscriptionProductId.PLUS, SubscriptionProductId.ESSENTIAL],
};

const DEFAULT_PRODUCT_BY_GROUP: Record<ProductGroup, SubscriptionProductId> = {
    cme: SubscriptionProductId.TRIAL_PREMIUM,
    legacy: SubscriptionProductId.PLUS,
};

const PRODUCT_GROUP_OPTIONS: Array<{ value: ProductGroup; label: string }> = [
    { value: 'cme', label: 'CME' },
    { value: 'legacy', label: 'Legacy' },
];

const SUBSCRIPTION_PRODUCT_LABELS: Record<SubscriptionProductId, string> = {
    [SubscriptionProductId.TRIAL_PREMIUM]: 'Premium Trial',
    [SubscriptionProductId.PREMIUM]: 'Premium',
    [SubscriptionProductId.STANDARD]: 'Fruitful',
    [SubscriptionProductId.PLUS]: 'Plus',
    [SubscriptionProductId.ESSENTIAL]: 'Essential',
    [SubscriptionProductId.JOINT]: 'Joint',
    [SubscriptionProductId.SOLO]: 'Solo',
};

const BILLING_FREQUENCY_LABELS: Record<MembershipBillingFrequency, string> = {
    [MembershipBillingFrequency.MONTHLY]: 'Monthly',
    [MembershipBillingFrequency.QUARTERLY]: 'Quarterly',
    [MembershipBillingFrequency.YEARLY]: 'Yearly',
};

const getProductOptions = (groupType: ProductGroup) =>
    PRODUCTS_BY_GROUP[groupType].map((id) => ({
        value: id,
        label: SUBSCRIPTION_PRODUCT_LABELS[id],
    }));

const getBillingFrequencyOptions = (productId: SubscriptionProductId) =>
    SUBSCRIPTION_PRODUCT_BILLING_FREQUENCIES[productId].map((value) => ({
        value,
        label: BILLING_FREQUENCY_LABELS[value],
    }));

const getProductGroup = (productId: SubscriptionProductId): ProductGroup =>
    PRODUCTS_BY_GROUP.cme.includes(productId) ? 'cme' : 'legacy';

interface QuickSwitcherUser {
    id: string;
    name: string;
    email: string;
    password: string;
    // Recorded on this device when the user is created here (or when their
    // subscription is changed here). Absent for manually added logins, which
    // fall back to the defaults in the change-subscription sheet.
    subscriptionProductId?: SubscriptionProductId;
    billingFrequency?: MembershipBillingFrequency;
}

export default function QuickSwitcherPage() {
    const stytch = useStytch();
    const toast = useToastController();

    // Use environment variables to force exclusion from prod bundles
    if (
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'local' &&
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'sandbox' &&
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'staging'
    ) {
        return <Redirect href="/" />;
    }

    const [users, setUsers] = useState<QuickSwitcherUser[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUserSheetOpen, setIsUserSheetOpen] = useState(false);
    const [userSheetMode, setUserSheetMode] =
        useState<UserSheetMode>('create_user');
    const [error, setError] = useState<string | null>(null);
    const [newUserName, setNewUserName] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');

    // Create test user form state
    const [createEmail, setCreateEmail] = useState('');
    const [createFirstName, setCreateFirstName] = useState('');
    const [createLastName, setCreateLastName] = useState('');
    const [createPassword, setCreatePassword] = useState('');
    const [createProductGroup, setCreateProductGroup] =
        useState<ProductGroup>('cme');
    const [createProductId, setCreateProductId] =
        useState<SubscriptionProductId>(DEFAULT_PRODUCT_BY_GROUP.cme);
    const [createBillingFrequency, setCreateBillingFrequency] =
        useState<MembershipBillingFrequency>(
            getDefaultBillingFrequency(DEFAULT_PRODUCT_BY_GROUP.cme),
        );
    const [createWithPlaceholderMoneyMap, setCreateWithPlaceholderMoneyMap] =
        useState(false);

    const { mutate: createTestUser, isPending: isCreating } =
        useCreateTestUser();

    // Change subscription sheet state
    const [isChangeSubscriptionSheetOpen, setIsChangeSubscriptionSheetOpen] =
        useState(false);
    const [changeSubscriptionUser, setChangeSubscriptionUser] =
        useState<QuickSwitcherUser | null>(null);
    const [changeProductGroup, setChangeProductGroup] =
        useState<ProductGroup>('cme');
    const [changeProductId, setChangeProductId] =
        useState<SubscriptionProductId>(DEFAULT_PRODUCT_BY_GROUP.cme);
    const [changeBillingFrequency, setChangeBillingFrequency] =
        useState<MembershipBillingFrequency>(
            getDefaultBillingFrequency(DEFAULT_PRODUCT_BY_GROUP.cme),
        );

    const { mutate: changeTestUserSubscription, isPending: isChanging } =
        useChangeTestUserSubscription();

    // Load users from StorageFacade (MMKV on native, localStorage on web) on mount
    useEffect(() => {
        const stored = StorageFacade.getString(QUICK_SWITCHER_STORAGE_KEY);
        if (stored != null) {
            try {
                setUsers(JSON.parse(stored) as QuickSwitcherUser[]);
            } catch (e) {
                console.error('Failed to parse stored users', e);
            }
        }
    }, []);

    // Persist users whenever they change
    const saveUsers = useCallback((updatedUsers: QuickSwitcherUser[]) => {
        StorageFacade.setString(
            QUICK_SWITCHER_STORAGE_KEY,
            JSON.stringify(updatedUsers),
        );
        setUsers(updatedUsers);
    }, []);

    const handleSwitchUser = useCallback(
        async (user: QuickSwitcherUser) => {
            setIsLoading(true);
            setError(null);

            try {
                const response = await stytch.passwords.authenticate({
                    email: user.email,
                    password: user.password,
                    session_duration_minutes: SESSION_DURATION_MINUTES,
                });

                if (response.status_code !== 200) {
                    setError(
                        `Login failed with status ${response.status_code}`,
                    );
                } else if (isWeb) {
                    window.open('/home', '_blank');
                } else {
                    router.replace('/home');
                }
            } catch (err) {
                const stytchError = err as StytchError;
                setError(
                    stytchError.message ||
                        'Authentication failed. Please check credentials.',
                );
            } finally {
                setIsLoading(false);
            }
        },
        [stytch, isWeb],
    );

    const handleAddUser = useCallback(() => {
        if (!newUserName || !newUserEmail || !newUserPassword) {
            setError('All fields are required');
            return;
        }

        const newUser: QuickSwitcherUser = {
            id: Date.now().toString(),
            name: newUserName,
            email: newUserEmail,
            password: newUserPassword,
        };

        const updatedUsers = [...users, newUser];
        saveUsers(updatedUsers);

        // Reset form
        setNewUserName('');
        setNewUserEmail('');
        setNewUserPassword('');
        setIsUserSheetOpen(false);
        setError(null);
    }, [newUserName, newUserEmail, newUserPassword, users, saveUsers]);

    const handleDeleteUser = useCallback(
        (userId: string) => {
            const updatedUsers = users.filter((u) => u.id !== userId);
            saveUsers(updatedUsers);
        },
        [users, saveUsers],
    );

    const handleCreateTestUser = useCallback(() => {
        if (!createEmail) {
            setError('Email is required');
            return;
        }

        setError(null);

        createTestUser(
            {
                body: {
                    email: createEmail,
                    ...(createFirstName && { firstName: createFirstName }),
                    ...(createLastName && { lastName: createLastName }),
                    ...(createPassword && { password: createPassword }),
                    subscriptionProductId: createProductId,
                    billingFrequency: createBillingFrequency,
                    withPlaceholderMoneyMap:
                        createProductGroup !== 'legacy' &&
                        createWithPlaceholderMoneyMap,
                },
            },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        // Add the created user to the quick-switcher list
                        const displayName =
                            [response.user.firstName, response.user.lastName]
                                .filter(Boolean)
                                .join(' ') || response.credentials.email;

                        const newUser: QuickSwitcherUser = {
                            id: response.user.id,
                            name: displayName,
                            email: response.credentials.email,
                            password: response.credentials.password,
                            subscriptionProductId: createProductId,
                            billingFrequency: createBillingFrequency,
                        };

                        const updatedUsers = [...users, newUser];
                        saveUsers(updatedUsers);

                        // Reset form and close modal
                        setCreateEmail('');
                        setCreateFirstName('');
                        setCreateLastName('');
                        setCreatePassword('');
                        setCreateProductGroup('cme');
                        setCreateProductId(DEFAULT_PRODUCT_BY_GROUP.cme);
                        setCreateBillingFrequency(
                            getDefaultBillingFrequency(
                                DEFAULT_PRODUCT_BY_GROUP.cme,
                            ),
                        );
                        setCreateWithPlaceholderMoneyMap(false);
                        setIsUserSheetOpen(false);

                        // Show success toast
                        toast.show('Test user created successfully!', {
                            duration: SUCCESS_TOAST_DURATION,
                            icon: SuccessToastIcon,
                        });
                    } else {
                        setError('Failed to create test user');
                    }
                },
                onError: (err) => {
                    const errorMessage =
                        err instanceof Error
                            ? err.message
                            : 'Failed to create test user';
                    setError(errorMessage);
                },
            },
        );
    }, [
        createEmail,
        createFirstName,
        createLastName,
        createPassword,
        createProductGroup,
        createProductId,
        createBillingFrequency,
        createWithPlaceholderMoneyMap,
        users,
        saveUsers,
        createTestUser,
        toast,
    ]);

    const handleChangeSubscription = useCallback(() => {
        if (changeSubscriptionUser == null) {
            return;
        }

        setError(null);

        changeTestUserSubscription(
            {
                body: {
                    email: changeSubscriptionUser.email,
                    membershipType: changeProductId,
                    billingFrequency: changeBillingFrequency,
                },
            },
            {
                onSuccess: () => {
                    saveUsers(
                        users.map((user) =>
                            user.id === changeSubscriptionUser.id
                                ? {
                                      ...user,
                                      subscriptionProductId: changeProductId,
                                      billingFrequency: changeBillingFrequency,
                                  }
                                : user,
                        ),
                    );
                    setIsChangeSubscriptionSheetOpen(false);
                    setChangeSubscriptionUser(null);

                    toast.show('Subscription updated successfully!', {
                        duration: SUCCESS_TOAST_DURATION,
                        icon: SuccessToastIcon,
                    });
                },
                onError: (err) => {
                    const errorMessage =
                        err instanceof Error
                            ? err.message
                            : 'Failed to change subscription';
                    setError(errorMessage);
                },
            },
        );
    }, [
        changeSubscriptionUser,
        changeProductId,
        changeBillingFrequency,
        changeTestUserSubscription,
        users,
        saveUsers,
        toast,
    ]);

    const handleOpenChangeSubscription = useCallback(
        (user: QuickSwitcherUser) => {
            const productId =
                user.subscriptionProductId ?? DEFAULT_PRODUCT_BY_GROUP.cme;

            setChangeSubscriptionUser(user);
            setChangeProductGroup(getProductGroup(productId));
            setChangeProductId(productId);
            setChangeBillingFrequency(
                user.billingFrequency ?? getDefaultBillingFrequency(productId),
            );
            setIsChangeSubscriptionSheetOpen(true);
        },
        [],
    );

    if (isLoading) {
        return <FullScreenSpinner />;
    }

    return (
        <>
            <ScreenContainer
                title="Quick Switch"
                subtitle="Switch between test users quickly."
                hasBackButton
                onFallbackRoute="/dev"
                rightAction={
                    <IconButton
                        variant="primary"
                        icon={UserPlusIcon}
                        onPress={() => {
                            setUserSheetMode('create_user');
                            setIsUserSheetOpen(true);
                        }}
                    />
                }
            >
                <YStack gap={24} width="100%">
                    {error != null && (
                        <Notice
                            variant="danger"
                            title="Something went wrong"
                            description={error}
                        />
                    )}

                    <RowList
                        width="100%"
                        rowHeaderProps={{
                            title: 'Saved logins',
                        }}
                    >
                        {users.length === 0 ? (
                            <YStack padding={16} width="100%">
                                <Notice
                                    variant="neutral"
                                    title="No users saved yet"
                                    description="Use the + button in the header to create a test user or add saved credentials on this device."
                                />
                            </YStack>
                        ) : (
                            users.map((user) => (
                                <Row
                                    key={user.id}
                                    title={user.name}
                                    subtitle={user.email}
                                    onPress={() => {
                                        void handleSwitchUser(user);
                                    }}
                                    rightContent={
                                        <XStack gap={4}>
                                            <IconButton
                                                variant="tertiary"
                                                icon={CogIcon}
                                                onPress={(e) => {
                                                    e?.stopPropagation();
                                                    handleOpenChangeSubscription(
                                                        user,
                                                    );
                                                }}
                                            />
                                            <IconButton
                                                variant="tertiary"
                                                tone="danger"
                                                icon={TrashIcon}
                                                onPress={(e) => {
                                                    e?.stopPropagation();
                                                    handleDeleteUser(user.id);
                                                }}
                                            />
                                        </XStack>
                                    }
                                />
                            ))
                        )}
                    </RowList>
                </YStack>
            </ScreenContainer>

            <Sheet
                isOpen={isUserSheetOpen}
                onClose={() => {
                    setIsUserSheetOpen(false);
                }}
                title={
                    userSheetMode === 'create_user'
                        ? 'Create test user'
                        : 'Add saved login'
                }
                subtitle={
                    userSheetMode === 'create_user'
                        ? 'Provisions a new user with membership and money map.'
                        : 'Stores email and password on this device only.'
                }
                showCloseButton
                forceKeyboardCompatibleBottomSheet
            >
                <YStack gap={20} width="100%">
                    <SegmentedControl<UserSheetMode>
                        selected={userSheetMode}
                        defaultValue="create_user"
                        onValueChange={setUserSheetMode}
                        size="small"
                        options={[
                            {
                                value: 'create_user',
                                label: 'Create new',
                            },
                            {
                                value: 'add_credentials',
                                label: 'Add login',
                            },
                        ]}
                    />
                    {userSheetMode === 'add_credentials' ? (
                        <Form onSubmit={handleAddUser}>
                            <YStack gap={24} width="100%">
                                <YStack gap={8}>
                                    <LabelRegular>Name</LabelRegular>
                                    <PrivateInput
                                        placeholder="Display name"
                                        value={newUserName}
                                        onChangeText={setNewUserName}
                                        onSubmitEditing={handleAddUser}
                                    />
                                </YStack>
                                <YStack gap={8}>
                                    <LabelRegular>Email</LabelRegular>
                                    <PrivateInput
                                        placeholder="you@example.com"
                                        value={newUserEmail}
                                        onChangeText={setNewUserEmail}
                                        inputMode="email"
                                        keyboardType="email-address"
                                        onSubmitEditing={handleAddUser}
                                    />
                                </YStack>
                                <YStack gap={8}>
                                    <LabelRegular>Password</LabelRegular>
                                    <PrivateInput
                                        placeholder="Password"
                                        value={newUserPassword}
                                        onChangeText={setNewUserPassword}
                                        secureTextEntry
                                        onSubmitEditing={handleAddUser}
                                    />
                                </YStack>
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    text="Add credentials"
                                    onPress={handleAddUser}
                                />
                            </YStack>
                        </Form>
                    ) : (
                        <Form onSubmit={handleCreateTestUser}>
                            <YStack width="100%" gap={24}>
                                <YStack gap={8}>
                                    <LabelRegular>
                                        Email (required)
                                    </LabelRegular>
                                    <Input
                                        placeholder="you@example.com"
                                        value={createEmail}
                                        onChangeText={setCreateEmail}
                                        inputMode="email"
                                        keyboardType="email-address"
                                        onSubmitEditing={handleCreateTestUser}
                                    />
                                </YStack>
                                <YStack gap={8}>
                                    <LabelRegular>First name</LabelRegular>
                                    <Input
                                        placeholder="Optional"
                                        value={createFirstName}
                                        onChangeText={setCreateFirstName}
                                        onSubmitEditing={handleCreateTestUser}
                                    />
                                </YStack>
                                <YStack gap={8}>
                                    <LabelRegular>Last name</LabelRegular>
                                    <Input
                                        placeholder="Optional"
                                        value={createLastName}
                                        onChangeText={setCreateLastName}
                                        onSubmitEditing={handleCreateTestUser}
                                    />
                                </YStack>
                                <YStack gap={8}>
                                    <LabelRegular>Password</LabelRegular>
                                    <PrivateInput
                                        placeholder="Optional (blank = auto-generated)"
                                        value={createPassword}
                                        onChangeText={setCreatePassword}
                                        secureTextEntry
                                        onSubmitEditing={handleCreateTestUser}
                                    />
                                </YStack>
                                <YStack gap={8}>
                                    <LabelRegular>
                                        Subscription product group
                                    </LabelRegular>
                                    <SegmentedControl<ProductGroup>
                                        selected={createProductGroup}
                                        defaultValue="cme"
                                        onValueChange={(groupType) => {
                                            const productId =
                                                DEFAULT_PRODUCT_BY_GROUP[
                                                    groupType
                                                ];
                                            setCreateProductGroup(groupType);
                                            setCreateProductId(productId);
                                            setCreateBillingFrequency(
                                                getDefaultBillingFrequency(
                                                    productId,
                                                ),
                                            );
                                        }}
                                        size="small"
                                        options={PRODUCT_GROUP_OPTIONS}
                                    />
                                </YStack>
                                <YStack gap={8}>
                                    <LabelRegular>Product</LabelRegular>
                                    <SegmentedControl<SubscriptionProductId>
                                        selected={createProductId}
                                        defaultValue={
                                            DEFAULT_PRODUCT_BY_GROUP.cme
                                        }
                                        onValueChange={(productId) => {
                                            setCreateProductId(productId);
                                            setCreateBillingFrequency(
                                                getDefaultBillingFrequency(
                                                    productId,
                                                ),
                                            );
                                        }}
                                        size="small"
                                        options={getProductOptions(
                                            createProductGroup,
                                        )}
                                    />
                                </YStack>
                                {getBillingFrequencyOptions(createProductId)
                                    .length > 1 && (
                                    <YStack gap={8}>
                                        <LabelRegular>
                                            Billing frequency
                                        </LabelRegular>
                                        <SegmentedControl<MembershipBillingFrequency>
                                            selected={createBillingFrequency}
                                            defaultValue={getDefaultBillingFrequency(
                                                createProductId,
                                            )}
                                            onValueChange={
                                                setCreateBillingFrequency
                                            }
                                            size="small"
                                            options={getBillingFrequencyOptions(
                                                createProductId,
                                            )}
                                        />
                                    </YStack>
                                )}
                                {createProductGroup !== 'legacy' && (
                                    <XStack
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <LabelRegular>
                                            Generate placeholder draft money map
                                        </LabelRegular>
                                        <Switch
                                            isChecked={
                                                createWithPlaceholderMoneyMap
                                            }
                                            onCheckedChange={
                                                setCreateWithPlaceholderMoneyMap
                                            }
                                        />
                                    </XStack>
                                )}
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    text={
                                        isCreating
                                            ? 'Creating...'
                                            : 'Create test user'
                                    }
                                    onPress={handleCreateTestUser}
                                    disabled={isCreating}
                                />
                            </YStack>
                        </Form>
                    )}
                </YStack>
            </Sheet>

            <Sheet
                isOpen={isChangeSubscriptionSheetOpen}
                onClose={() => {
                    setIsChangeSubscriptionSheetOpen(false);
                    setChangeSubscriptionUser(null);
                }}
                title="Change subscription"
                subtitle={
                    changeSubscriptionUser != null
                        ? `Update the subscription for ${changeSubscriptionUser.name}.`
                        : undefined
                }
                showCloseButton
                forceKeyboardCompatibleBottomSheet
            >
                <YStack gap={20} width="100%">
                    <YStack gap={8}>
                        <LabelRegular>Subscription product group</LabelRegular>
                        <SegmentedControl<ProductGroup>
                            selected={changeProductGroup}
                            defaultValue="cme"
                            onValueChange={(group) => {
                                const productId =
                                    DEFAULT_PRODUCT_BY_GROUP[group];
                                setChangeProductGroup(group);
                                setChangeProductId(productId);
                                setChangeBillingFrequency(
                                    getDefaultBillingFrequency(productId),
                                );
                            }}
                            size="small"
                            options={PRODUCT_GROUP_OPTIONS}
                        />
                    </YStack>
                    <YStack gap={8}>
                        <LabelRegular>Product</LabelRegular>
                        <SegmentedControl<SubscriptionProductId>
                            selected={changeProductId}
                            defaultValue={DEFAULT_PRODUCT_BY_GROUP.cme}
                            onValueChange={(productId) => {
                                setChangeProductId(productId);
                                setChangeBillingFrequency(
                                    getDefaultBillingFrequency(productId),
                                );
                            }}
                            size="small"
                            options={getProductOptions(changeProductGroup)}
                        />
                    </YStack>
                    {getBillingFrequencyOptions(changeProductId).length > 1 && (
                        <YStack gap={8}>
                            <LabelRegular>Billing frequency</LabelRegular>
                            <SegmentedControl<MembershipBillingFrequency>
                                selected={changeBillingFrequency}
                                defaultValue={getDefaultBillingFrequency(
                                    changeProductId,
                                )}
                                onValueChange={setChangeBillingFrequency}
                                size="small"
                                options={getBillingFrequencyOptions(
                                    changeProductId,
                                )}
                            />
                        </YStack>
                    )}
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        text={
                            isChanging ? 'Updating...' : 'Update subscription'
                        }
                        onPress={handleChangeSubscription}
                        disabled={isChanging}
                    />
                </YStack>
            </Sheet>
        </>
    );
}
